/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState } from 'react';

import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Button,
  Tfoot,
  Progress,
} from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';
import { isRequestFailure, PaginationType } from '../../hooks/useFetch';
import { Paginator } from '../paginator';
import { CreateButton } from '../buttons/CreateButton';
import { EditButton } from '../buttons/EditButton';
import { ViewButton } from '../buttons/ViewButton';
import { DataSkeleton, EmptyState, ErrorState } from '../states';
import AlertModal from '../AlertModal';
import { DeleteButton } from '../buttons/DeleteButton';

interface RowActionsProps {
  show?: boolean;
  edit?: boolean;
  delete?: boolean;
}

interface FormatColumnProps {
  field: string;
  formatField: any;
}

interface ColumnsProps {
  name: string;
  label: string | any[];
}

interface DataGridProps {
  columns: ColumnsProps[];
  rows: any;
  requestApi: any;
  error: any;
  baseUrl: string;
  data: any;
  loading: boolean;
  hasCreate?: boolean;
  deleteAlertMsg?: any;
  infoButton?: any[];
  columnWithAction?: any[];
  rowActions?: RowActionsProps;
  headerFilters?: any[];
  formatColum?: FormatColumnProps[];
  emptyMessage?: JSX.Element;
}

export const DataGrid = ({
  columns,
  rows,
  requestApi,
  error,
  baseUrl,
  data,
  loading,
  hasCreate,
  deleteAlertMsg,
  infoButton,
  columnWithAction,
  rowActions,
  headerFilters,
  formatColum = [],
  emptyMessage,
}: DataGridProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationType>({} as PaginationType);
  const [update, setUpdate] = useState(false);
  const [queryParams, setQueryParams] = useState<any>({
    page: pagination?.current_page ?? 1,
    per_page: 10,
  });

  const hasRowActions = rowActions?.show || rowActions?.edit || rowActions?.delete;
  const activeFilterEntries = Object.entries(queryParams).filter(
    ([key, value]) => !['page', 'per_page'].includes(key) && value !== '' && value != null
  );
  const hasActiveFilters = activeFilterEntries.length > 0;
  const pathEntity =
    [
      ['students', 'alunos'],
      ['teachers', 'professores'],
      ['tutors', 'tutores'],
      ['responsible', 'responsáveis'],
      ['secretaries', 'secretários'],
      ['notifications', 'notificações'],
      ['assessments', 'avaliações'],
      ['topics', 'tópicos'],
      ['categories', 'categorias'],
      ['meetings', 'encontros'],
      ['weeks', 'semanas'],
      ['modules', 'módulos'],
      ['templates', 'modelos'],
      ['extra-materials', 'materiais extras'],
      ['institutions', 'instituições'],
      ['teams', 'turmas'],
      ['users', 'usuários'],
    ].find(([path]) => location.pathname.includes(path))?.[1] || 'registros';
  const clearFilters = () => {
    headerFilters?.forEach((filter) => filter?.setValue?.(null));
    setQueryParams({ page: 1, per_page: 10 });
  };
  const createItem = () => navigate(`${location.pathname}/create`, { replace: true });
  const retry = () => requestApi('get', { params: queryParams });
  const getEmptyAction = () => {
    if (hasActiveFilters) return { label: 'Limpar filtros', onClick: clearFilters };
    return undefined;
  };

  const renderEmptyState = () => {
    if (emptyMessage) {
      return (
        <Flex mt="20px" align="center" justify="center">
          {emptyMessage}
        </Flex>
      );
    }

    return (
      <EmptyState
        title={
          hasActiveFilters ? 'Nenhum resultado para estes filtros' : 'Nenhum resultado encontrado'
        }
        description={
          hasActiveFilters
            ? `Altere ou remova os filtros para localizar ${pathEntity}.`
            : `Não existem ${pathEntity} disponíveis para exibição.`
        }
        action={getEmptyAction()}
      />
    );
  };

  const handleDeleteItem = async (itemId: string) => {
    const result = await requestApi('delete', null, `${baseUrl.split('?')[0]}/${itemId}`);
    if (!isRequestFailure(result)) setUpdate((old) => !old);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      requestApi('get', { params: queryParams });
    }, 250);

    return () => {
      clearTimeout(timeout);
    };
  }, [queryParams, update]);

  useEffect(() => {
    setPagination(data?.pagination);
  }, [data]);

  return (
    <Box>
      {!headerFilters && hasCreate && (
        <Flex justify="flex-end" mb={5}>
          <CreateButton onClick={createItem}>Criar</CreateButton>
        </Flex>
      )}
      {headerFilters && (
        <>
          <Flex mb={5} align="center">
            <Flex flexWrap="wrap" align="center" w="79%" gap={5}>
              {headerFilters
                ?.filter((filter) => filter?.isMain)
                ?.map((filterProps) => (
                  <Box key={filterProps.name} w="79%">
                    <filterProps.filterComponent
                      isDisabled={
                        data?.data?.length === 0 && !queryParams[filterProps.name]?.toString()
                      }
                      onChange={(value) =>
                        setQueryParams((old) => ({ ...old, [filterProps.name]: value }))
                      }
                      {...(filterProps?.onInputChange
                        ? { onInputChange: filterProps.onInputChange }
                        : {})}
                      {...(filterProps?.isSearchable != null
                        ? { isSearchable: filterProps.isSearchable }
                        : {})}
                      {...(filterProps?.setValue ? { setValue: filterProps.setValue } : {})}
                      value={queryParams[filterProps.name] ?? ''}
                      placeholder={filterProps?.placeholder ?? 'Buscar'}
                      options={filterProps?.options}
                    />
                  </Box>
                ))}
            </Flex>
          </Flex>
          <Flex flexWrap="wrap" align="center" mb={5} gap={5}>
            {headerFilters
              ?.filter((filter) => !filter?.isMain)
              ?.map((filterProps) => (
                <Box key={filterProps.name} flex={1}>
                  <filterProps.filterComponent
                    isDisabled={
                      data?.data?.length === 0 && !queryParams[filterProps.name]?.toString()
                    }
                    onChange={(value) =>
                      setQueryParams((old) => ({ ...old, [filterProps.name]: value, page: 1 }))
                    }
                    {...(filterProps?.onInputChange
                      ? { onInputChange: filterProps.onInputChange }
                      : {})}
                    {...(filterProps?.isSearchable != null
                      ? { isSearchable: filterProps.isSearchable }
                      : {})}
                    {...(filterProps?.setValue ? { setValue: filterProps.setValue } : {})}
                    value={queryParams[filterProps.name] ?? ''}
                    placeholder={filterProps?.placeholder ?? 'Buscar'}
                    options={filterProps?.options}
                  />
                </Box>
              ))}
            {hasCreate && (
              <Flex align="center" marginLeft="auto">
                <CreateButton onClick={createItem}>Criar</CreateButton>
              </Flex>
            )}
          </Flex>
        </>
      )}

      {(loading || (!data && !error)) && !data?.data?.length ? (
        <DataSkeleton variant="table" rows={6} columns={columns.length + (hasRowActions ? 1 : 0)} />
      ) : (
        <>
          {loading && data?.data?.length > 0 && (
            <Progress
              size="xs"
              isIndeterminate
              colorScheme="blue"
              mb={2}
              aria-label="Atualizando dados"
            />
          )}
          {error && !data?.data?.length && (
            <ErrorState
              description={error?.msg || error?.message || 'Verifique a conexão e tente novamente.'}
              onRetry={retry}
            />
          )}
          {data?.data?.length > 0 && (
            <>
              {error && (
                <Box mb={4}>
                  <ErrorState
                    title="Não foi possível atualizar os dados"
                    description="Os dados anteriores continuam disponíveis. Tente novamente."
                    onRetry={retry}
                  />
                </Box>
              )}
              <TableContainer
                whiteSpace="break-spaces"
                background="white"
                boxShadow="0px 3px 7px #00000050"
              >
                <Table variant="striped" colorScheme="blackAlpha">
                  <Thead>
                    <Tr>
                      {columns?.map((item) => (
                        <Th key={item.name}>
                          <Flex align="center">
                            {item.label}
                            {infoButton?.find((button) => button.name === item.name)?.button}
                          </Flex>
                        </Th>
                      ))}
                      {hasRowActions && <Th>Ações</Th>}
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows?.map((item) => (
                      <Tr key={item.id}>
                        {columns?.map((dataItem) => (
                          <Td key={dataItem.name} maxW="250px">
                            {formatColum.length > 0
                              ? formatColum
                                  .find((value) => value.field === dataItem.name)
                                  ?.formatField(_.get(item, dataItem.name)) ??
                                columnWithAction
                                  ?.find((value) => value.name === dataItem.name)
                                  ?.columnAction(item) ??
                                _.get(item, dataItem.name)
                              : columnWithAction
                                  ?.find((value) => value.name === dataItem.name)
                                  ?.columnAction(item) ?? _.get(item, dataItem.name)}
                          </Td>
                        ))}
                        {hasRowActions && (
                          <Td>
                            <Flex>
                              {rowActions?.show && (
                                <ViewButton
                                  onClick={() => {
                                    navigate(`${location.pathname}/${item.id}`, {
                                      replace: true,
                                    });
                                  }}
                                />
                              )}

                              {rowActions?.edit && (
                                <EditButton
                                  onClick={() => {
                                    navigate(`${location.pathname}/${item.id}/edit`, {
                                      replace: true,
                                    });
                                  }}
                                />
                              )}

                              {rowActions?.delete && (
                                <AlertModal
                                  alertMsg={deleteAlertMsg}
                                  msgStatus="warning"
                                  ButtonOpen={DeleteButton}
                                  ConfirmButton={
                                    <Button
                                      w="200px"
                                      boxShadow="0px 2px 4px #00000050"
                                      onClick={() => handleDeleteItem(item.id)}
                                    >
                                      Confirmar exclusão
                                    </Button>
                                  }
                                />
                              )}
                            </Flex>
                          </Td>
                        )}
                      </Tr>
                    ))}
                  </Tbody>
                  {pagination?.total_items > 10 && (
                    <Tfoot>
                      <Tr>
                        <Td
                          colSpan={
                            hasRowActions ? Number(columns?.length) + 1 : Number(columns?.length)
                          }
                          alignItems="end"
                        >
                          <Paginator
                            pagination={pagination}
                            queryParams={queryParams}
                            setQueryParams={setQueryParams}
                          />
                        </Td>
                      </Tr>
                    </Tfoot>
                  )}
                </Table>
              </TableContainer>
            </>
          )}
          {!error && !data?.data?.length && renderEmptyState()}
        </>
      )}
    </Box>
  );
};
